import React, { useEffect, useState } from 'react';
import { collection, query, orderBy, onSnapshot, getDocs } from 'firebase/firestore';
import { db } from '../firebase/firebase';

const ProgrammePreview = ({ projectId, onClick }) => {
  const [programmeDocs, setProgrammeDocs] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [projectTeams, setProjectTeams] = useState([]);
  const [loading, setLoading] = useState(true);

  // Fetch project teams
  useEffect(() => {
    if (!projectId) return;

    const fetchTeams = async () => {
      try {
        const teamsSnapshot = await getDocs(collection(db, `projects/${projectId}/teams`));
        const teams = teamsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setProjectTeams(teams);
      } catch (error) {
        console.error('Error fetching teams:', error);
      }
    };

    fetchTeams();
  }, [projectId]);

  // Fetch programme documents
  useEffect(() => {
    if (!projectId) return;

    const unsubscribe = onSnapshot(
      query(
        collection(db, `projects/${projectId}/programme`),
        orderBy('uploadedAt', 'desc')
      ),
      (snapshot) => {
        if (!snapshot.empty) {
          const docs = snapshot.docs.map(doc => {
            const data = doc.data();
            return {
              id: doc.id,
              name: data.originalName || data.fileName,
              type: data.fileType,
              uploadedAt: new Date(data.uploadedAt),
              url: data.downloadURL,
              teams: data.teams || [],
              itemType: 'file'
            };
          });
          setProgrammeDocs(docs);
        } else {
          setProgrammeDocs([]);
        }
      },
      (error) => {
        console.error('Error fetching programme files:', error);
      }
    );

    return () => unsubscribe();
  }, [projectId]);

  // Fetch tasks (without orderBy since createdAt might not exist)
  useEffect(() => {
    if (!projectId) return;

    const unsubscribe = onSnapshot(
      collection(db, `projects/${projectId}/tasks`),
      (snapshot) => {
        if (!snapshot.empty) {
          const tasksData = snapshot.docs.map(doc => {
            const data = doc.data();
            // Use start date as fallback for sorting if createdAt doesn't exist
            const sortDate = data.createdAt ? new Date(data.createdAt) : new Date(data.start);
            return {
              id: doc.id,
              name: data.name,
              team: data.team,
              start: data.start,
              end: data.end,
              createdAt: sortDate,
              itemType: 'task'
            };
          });
          setTasks(tasksData);
        } else {
          setTasks([]);
        }
        setLoading(false);
      },
      (error) => {
        console.error('Error fetching tasks:', error);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [projectId]);

  const getTimeAgo = (timestamp) => {
    if (!timestamp) return '';
    const now = new Date();
    const diffInMinutes = Math.floor((now - timestamp) / (1000 * 60));
    
    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`;
    return `${Math.floor(diffInMinutes / 1440)}d ago`;
  };

  const formatDateRange = (start, end) => {
    if (!start || !end) return 'No dates set';
    const startDate = new Date(start);
    const endDate = new Date(end);
    
    const formatDate = (date) => {
      return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
    };
    
    return `${formatDate(startDate)} - ${formatDate(endDate)}`;
  };

  const getFileTypeInfo = (type) => {
    const typeStr = (type || '').toLowerCase();
    if (typeStr === 'pdf') {
      return { bg: '#fef2f2', border: '#fecaca', color: '#dc2626', label: 'PDF' };
    } else if (typeStr === 'doc' || typeStr === 'docx') {
      return { bg: 'var(--branding-app-primary-soft)', border: 'var(--branding-app-primary)', color: 'var(--branding-app-primary)', label: 'DOC' };
    } else if (typeStr === 'png' || typeStr === 'jpg' || typeStr === 'jpeg') {
      return { bg: '#f0fdf4', border: '#bbf7d0', color: '#16a34a', label: 'IMG' };
    } else {
      return {
        bg: 'var(--branding-app-surface-panel)',
        border: 'var(--branding-app-surface-panel-border)',
        color: 'var(--branding-app-surface-panel-muted)',
        label: 'FILE',
      };
    }
  };

  const getTeamsDisplay = (docTeams) => {
    if (!docTeams || docTeams.length === 0) {
      return 'No teams assigned';
    }
    
    // Check if all project teams are assigned to this document
    if (projectTeams.length > 0 && docTeams.length === projectTeams.length) {
      // Compare team IDs to see if all teams are included
      const docTeamIds = new Set(docTeams.map(t => t.id));
      const allTeamsAssigned = projectTeams.every(team => docTeamIds.has(team.id));
      
      if (allTeamsAssigned) {
        return 'All teams';
      }
    }
    
    // Show team names
    if (docTeams.length === 1) {
      return docTeams[0].name;
    } else if (docTeams.length <= 3) {
      return docTeams.map(t => t.name).join(', ');
    } else {
      return `${docTeams.length} teams`;
    }
  };

  if (loading) {
    return (
      <div style={{
        height: '120px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'var(--branding-app-surface-panel-hint)',
        fontSize: '14px'
      }}>
        Loading...
      </div>
    );
  }

  // Combine files and tasks, sort by date
  const allItems = [...programmeDocs, ...tasks].sort((a, b) => {
    const dateA = a.itemType === 'file' ? a.uploadedAt : a.createdAt;
    const dateB = b.itemType === 'file' ? b.uploadedAt : b.createdAt;
    return dateB - dateA;
  });

  if (allItems.length === 0) {
    return (
      <div style={{
        height: '120px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'var(--branding-app-surface-panel-hint)',
        fontSize: '14px'
      }}>
        No programme files or tasks assigned
      </div>
    );
  }

  return (
    <div 
      style={{
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        background: 'transparent',
        padding: '16px',
        transition: 'all 0.2s ease'
      }}
    >
      <div style={{
        flex: 1,
        minHeight: 0,
        overflowY: 'auto',
        paddingRight: '8px'
      }}>
      {allItems.map((item, index) => {
          if (item.itemType === 'file') {
            const fileType = getFileTypeInfo(item.type);
            
            return (
              <div 
                key={item.id}
                style={{ 
                  display: 'flex', 
                  alignItems: 'flex-start', 
                  gap: '12px', 
                  marginBottom: index < allItems.length - 1 ? '12px' : '0',
                  paddingBottom: index < allItems.length - 1 ? '12px' : '0',
                  borderBottom: index < allItems.length - 1 ? '1px solid var(--branding-app-surface)' : 'none',
                  padding: '8px',
                  borderRadius: '8px'
                }}
              >
                {/* File Type Icon */}
                <div style={{
                  width: '32px',
                  height: '32px',
                  background: fileType.bg,
                  border: `1px solid ${fileType.border}`,
                  borderRadius: '6px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '10px',
                  fontWeight: '700',
                  color: fileType.color,
                  flexShrink: 0
                }}>
                  {fileType.label}
                </div>
                
                {/* Document Info */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    fontSize: '14px',
                    fontWeight: '600',
                    color: 'var(--branding-app-surface-panel-fg)',
                    marginBottom: '4px',
                    lineHeight: '1.3',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}>
                    {item.name}
                  </div>
                  
                  <div style={{
                    fontSize: '13px',
                    color: 'var(--branding-app-surface-panel-sub)',
                    marginBottom: '8px',
                    lineHeight: '1.3',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}>
                    {getTeamsDisplay(item.teams)}
                  </div>
                  
                  <div style={{
                    fontSize: '12px',
                    color: 'var(--branding-app-surface-panel-hint)'
                  }}>
                    {getTimeAgo(item.uploadedAt)}
                  </div>
                </div>
              </div>
            );
          } else {
            // Task item
            return (
              <div 
                key={item.id}
                style={{ 
                  display: 'flex', 
                  alignItems: 'flex-start', 
                  gap: '12px', 
                  marginBottom: index < allItems.length - 1 ? '12px' : '0',
                  paddingBottom: index < allItems.length - 1 ? '12px' : '0',
                  borderBottom: index < allItems.length - 1 ? '1px solid var(--branding-app-surface)' : 'none',
                  padding: '8px',
                  borderRadius: '8px'
                }}
              >
                {/* Task Icon */}
                <div style={{
                  width: '32px',
                  height: '32px',
                  background: '#f0f9ff',
                  border: '1px solid #bae6fd',
                  borderRadius: '6px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '10px',
                  fontWeight: '700',
                  color: '#0ea5e9',
                  flexShrink: 0
                }}>
                  TASK
                </div>
                
                {/* Task Info */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    fontSize: '14px',
                    fontWeight: '600',
                    color: 'var(--branding-app-surface-panel-fg)',
                    marginBottom: '4px',
                    lineHeight: '1.3',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}>
                    {item.name}
                  </div>
                  
                  <div style={{
                    fontSize: '13px',
                    color: 'var(--branding-app-surface-panel-sub)',
                    marginBottom: '8px',
                    lineHeight: '1.3',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}>
                    {item.team || 'No team assigned'}
                  </div>
                  
                  <div style={{
                    fontSize: '12px',
                    color: 'var(--branding-app-surface-panel-hint)'
                  }}>
                    {formatDateRange(item.start, item.end)}
                  </div>
                </div>
              </div>
            );
          }
        })}
      </div>
    </div>
  );
};

export default ProgrammePreview;
